/**
 * 与 docs/previewinfo_soa.md 一致；SOA 全名格式与 VehicleProperty.java（SusPreviewInfo_*）一致：
 * SOA::SusPreviewInfo_<MSG_PCM_...>
 * 标量 float 用于 previewInfo.ts 中 INS / 车速 / 垂向加速度等非 SusPreviewInfo 消息。
 */
import { getDisplayNameLabel } from "./displayNameLabels";
import { SUBSCRIBE_SIGNALS } from "./previewInfo";

export type PreviewSoaType = "uint8" | "uint8[10]" | "float[10]" | "float";

export type PreviewSoaRow = {
  displayName: string;
  zh: string;
  type: Exclude<PreviewSoaType, "float">;
};

/** 顺序与 previewinfo_soa.md 保持一致 */
export const PREVIEWINFO_SOA_ROWS: PreviewSoaRow[] = [
  { displayName: "MSG_PCM_FwdRoadFeat_FL", zh: "前方路面激励特征", type: "uint8[10]" },
  { displayName: "MSG_PCM_FwdRoadFeat_FR", zh: "前方路面激励特征-FR轮迹", type: "uint8[10]" },
  { displayName: "MSG_PCM_FwdRoadFeat_RL", zh: "前方路面激励特征-RL轮迹", type: "uint8[10]" },
  { displayName: "MSG_PCM_FwdRoadFeat_RR", zh: "前方路面激励特征-RR轮迹", type: "uint8[10]" },
  { displayName: "MSG_PCM_FwdRoadType", zh: "前方路面类型", type: "uint8" },
  { displayName: "MSG_PCM_SusObjHght_FL", zh: "目标物高度_FL轮迹", type: "float[10]" },
  { displayName: "MSG_PCM_SusObjHght_FR", zh: "目标物高度_FR轮迹", type: "float[10]" },
  { displayName: "MSG_PCM_SusObjHght_RL", zh: "目标物高度_RL轮迹", type: "float[10]" },
  { displayName: "MSG_PCM_SusObjHght_RR", zh: "目标物高度_RR轮迹", type: "float[10]" },
  { displayName: "MSG_PCM_SusObjLengh_FL", zh: "目标物宽度_FL轮迹", type: "float[10]" },
  { displayName: "MSG_PCM_SusObjLengh_FR", zh: "目标物宽度_FR轮迹", type: "float[10]" },
  { displayName: "MSG_PCM_SusObjLengh_RL", zh: "目标物宽度_RL轮迹", type: "float[10]" },
  { displayName: "MSG_PCM_SusObjLengh_RR", zh: "目标物宽度_RR轮迹", type: "float[10]" },
  { displayName: "MSG_PCM_SusObjDist_FL", zh: "目标物距离_FL轮迹", type: "float[10]" },
  { displayName: "MSG_PCM_SusObjDist_FR", zh: "目标物距离_FR轮迹", type: "float[10]" },
  { displayName: "MSG_PCM_SusObjDist_RL", zh: "目标物距离_RL轮迹", type: "float[10]" },
  { displayName: "MSG_PCM_SusObjDist_RR", zh: "目标物距离_RR轮迹", type: "float[10]" },
];

const SOA_PREFIX = "SOA::SusPreviewInfo_";

export function toPreviewSoaSignalName(displayName: string): string {
  return `${SOA_PREFIX}${displayName}`;
}

export function isArraySoaType(t: PreviewSoaType): boolean {
  return t.includes("[");
}

export function parseArrayLength(t: PreviewSoaType): number {
  const m = /\[(\d+)]/.exec(t);
  return m ? parseInt(m[1], 10) : 0;
}

/** 仅 float[] 数组（不含标量 float） */
export function isFloatSoaType(t: PreviewSoaType): boolean {
  return t.startsWith("float[");
}

/** HU cps_tool 页：与 previewInfo.SUBSCRIBE_SIGNALS 一致的全名 + 中文 + SOA 类型 */
export type PreviewHuCpsRow = {
  signalName: string;
  displayName: string;
  zh: string;
  type: PreviewSoaType;
};

const PREVIEW_SOA_TYPE_BY_DISPLAY_NAME: Record<string, PreviewSoaType> = {
  ...Object.fromEntries(PREVIEWINFO_SOA_ROWS.map((r) => [r.displayName, r.type])),
  INS_Latitude: "float",
  INS_Longitude: "float",
  INS_HeadingAngle: "float",
  VehSpd: "float",
  SusIMUAccVert: "float",
};

const ZH_BY_DISPLAY_NAME: Record<string, string> = Object.fromEntries(
  PREVIEWINFO_SOA_ROWS.map((r) => [r.displayName, r.zh])
);

const HU_CPS_ZH_OVERRIDES: Record<string, string> = {
  INS_Latitude: "INS 纬度",
  INS_Longitude: "INS 经度",
  INS_HeadingAngle: "INS 航向角",
  SusIMUAccVert: "悬架 IMU 垂向加速度",
};

function resolveHuCpsZh(displayName: string): string {
  return (
    HU_CPS_ZH_OVERRIDES[displayName] ??
    ZH_BY_DISPLAY_NAME[displayName] ??
    getDisplayNameLabel(displayName)
  );
}

export const PREVIEWINFO_HU_CPS_ROWS: PreviewHuCpsRow[] = SUBSCRIBE_SIGNALS.map(
  (s) => {
    const type = PREVIEW_SOA_TYPE_BY_DISPLAY_NAME[s.displayName];
    if (!type) {
      throw new Error(
        `previewinfoSoaCatalog: missing HU CPS type for displayName=${s.displayName}`
      );
    }
    return {
      signalName: s.signalName,
      displayName: s.displayName,
      zh: resolveHuCpsZh(s.displayName),
      type,
    };
  }
);

/** 标量初始值（与 previewInfo mock 典型值对齐，便于联调） */
export const HU_CPS_SCALAR_DEFAULTS: Partial<Record<string, number>> = {
  "SOA::INSInfo_MSG_InsLatitude": 40.1683718,
  "SOA::INSInfo_MSG_InsLongitude": 116.573723,
  "SOA::INSInfo_MSG_InsHeadingAngle": -96.918258,
};
